import { Box, Divider, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useDerivedEventEdit } from '@verantos-dev/app-state-cohort-builder';
import { ApiWorkflowDialog, DropdownListItem } from '@verantos-dev/shared-components';
import {
  DerivedEventInputFieldTypeLegacy,
  DerivedEventTypeLegacy,
  FilterTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { useDerivedEventCohortBuilderEditModalActions } from './hooks/useDerivedEventCohortBuilderEditModalActions';
import { CohortBuilderEditModalActions } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalActions/CohortBuilderEditModalActions';
import { CohortBuilderEditModalFilter } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilter/CohortBuilderEditModalFilter';
import { CohortBuilderEditModalFilterSection } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalFilterSection/CohortBuilderEditModalFilterSection';
import { CohortBuilderEditModalSummary } from '../../../../components/CohortBuilderEditModal/CohortBuilderEditModalSummary/CohortBuilderEditModalSummary';
import { CohortBuilderEditModalEvent } from '../../../../components/CohortBuilderEditModalEvent/CohortBuilderEditModalEvent';
import {
  CohortBuilderInputField,
  CohortBuilderInputFieldProps,
} from '../../../../components/CohortBuilderInputField/CohortBuilderInputField';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import { getCohortEditModalTitle } from '../../../../utils/getCohortEditModalTitle';

export const DerivedEventEditCard = () => {
  const theme = useTheme();
  const intl = useIntl();

  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const updateFieldError = (id: string, error?: string) => {
    setFieldErrors((prev) => {
      const updatedErrors = { ...prev };
      if (error) {
        updatedErrors[id] = error;
      } else {
        delete updatedErrors[id];
      }
      return updatedErrors;
    });
  };

  const {
    modal,
    name,
    type,
    eventFilters,
    temporalConstraints,
    uniquenessFilters,
    inputFieldConfigMap,
    isSaveAllowed,
    enableAddFilterButtons,
    anyOrderChecked,
    derivedEventIsInUse,
    hasAtLeastTwoSimpleEvents,
    typeInputFieldTitleText,
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters,
    onTypeChange,
    onAddCohortBuilderEventClick,
    onDeleteCohortBuilderEventClick,
    onAddTemporalConstraintClick,
    onDeleteTemporalConstraintClick,
    onAddUniquenessFilterClick,
    onDeleteUniquenessFilterClick,
    onFilterInputValueChange,
    onEditCohortBuilderEventInputFieldValue,
    onCancelDerivedEvent,
    onSaveDerivedEvent,
    onChangeAnyOrderCheckbox,
    headerRef,
    dialogContentRef,
    eventRef,
    temportalConstraintRef,
    uniquenessFilterRef,
    isEditable,
    viewMode,
  } = useDerivedEventEdit();
  const actions = useDerivedEventCohortBuilderEditModalActions({
    type,
    uniquenessFilters,
    onAddTemporalConstraintClick,
    onAddCohortBuilderEventClick,
    onAddUniquenessFilterClick,
  });
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);
  const numberOfEventFilters = eventFilters.length;

  // check if there are any errors in the derived event card
  const hasErrors = restrictSaveProcess ? Object.keys(fieldErrors).length > 0 : false;

  return (
    <ApiWorkflowDialog
      id="cohort-builder-derived-event-edit-modal"
      title={getCohortEditModalTitle(intl, modal ?? null, !!isEditable)}
      size="large"
      // isInitialLoading={isLoading}
      // initialLoadingError={conceptGroupsError}
      // onRetryInitialLoadingFetch={refetchConceptGroups}
      isValidForSubmit={isSaveAllowed && !nameInError && !hasErrors}
      onlyShowCloseButton={viewMode}
      dialogMessages={{
        entryCancelButtonText: viewMode
          ? intl.formatMessage({
              id: 'projects.cohortBuilderPage.simpleEventEditCard.cancelButtonText',
            })
          : undefined,
      }}
      onClose={onCancelDerivedEvent}
      onSubmit={() => onSaveDerivedEvent(emitResearchAssistantUpdate)} // TODO: update to call put endpoint
      // onSubmit={onSubmit}
      // isSubmitting={isSaveStudyArmLoading}
      // submittingError={saveStudyArmError}
      dialogContentStyleProps={{ paddingBottom: 0 }}
      ref={dialogContentRef as any}
      NonScrollBottomComponent={
        enableAddFilterButtons && !viewMode ? (
          <Box sx={(theme) => ({ pt: theme.spacing(4), px: theme.spacing(6) })}>
            <CohortBuilderEditModalActions id="subject-variable" actions={actions} />
          </Box>
        ) : null
      }
    >
      <Box data-testid={'header'} ref={headerRef}>
        <CohortBuilderEditModalSummary<DerivedEventTypeLegacy>
          id="simple-event-summary"
          nameProps={{
            displayLabel: intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.nameLabel',
            }),
            placeholder: intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.namePlaceholder',
            }),
            value: name,
            error: nameInError,
            helperText: nameInError ? nameErrorMsg : undefined,
            onChange: onNameChange,
            onBlur: onNameBlur,
            validCharacters: nameValidCharacters,
            viewMode: viewMode,
          }}
          typeProps={{
            id: 'derived-event-type',
            title: typeInputFieldTitleText || '',
            label: intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.typeLabel',
            }),
            placeholder: intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.typePlaceholder',
            }),
            value: type || '',
            onChange: (e) => onTypeChange(e.target.name, e.target.value as DerivedEventTypeLegacy),
            options: hasAtLeastTwoSimpleEvents
              ? [
                  DerivedEventTypeLegacy.MERGED,
                  DerivedEventTypeLegacy.SEQUENCE,
                  DerivedEventTypeLegacy.INSTANCE,
                ]
              : [DerivedEventTypeLegacy.SEQUENCE, DerivedEventTypeLegacy.INSTANCE],
          }}
          isCohortBuilderItemInUse={derivedEventIsInUse}
          viewMode={viewMode}
        />
        <Divider
          dir="horizontal"
          sx={{ width: '100%', height: '1px', my: theme.spacing(4), color: theme.palette.grey[50] }}
        />
      </Box>

      {/* render events filters */}
      {eventFilters.length > 0 && (
        <Box data-testid={'derivedEventFilters'} ref={eventRef}>
          <CohortBuilderEditModalFilterSection
            id="events"
            key="events"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.titleEvents',
            })}
            showCheckbox={type === DerivedEventTypeLegacy.SEQUENCE}
            checkboxDisplayLabel={intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.titleOrder',
            })}
            checked={anyOrderChecked}
            onChangeCheckbox={onChangeAnyOrderCheckbox}
            viewMode={viewMode}
          >
            {eventFilters.map(
              ({
                id: eventFilterId,
                eventName: eventNameValue,
                eventDateAttribute,
                isSelected,
                position,
                index,
              }) => {
                if (!inputFieldConfigMap[eventNameValue.id]) {
                  throw new Error(
                    `1 Input field config not found for input field id: ${eventNameValue.id}`
                  );
                }
                if (eventDateAttribute && !inputFieldConfigMap[eventDateAttribute.id]) {
                  throw new Error(
                    `2 Input field config not found for input field id: ${eventDateAttribute.id}`
                  );
                }
                if (isSelected && !inputFieldConfigMap[isSelected.id]) {
                  throw new Error(
                    `3 Input field config not found for input field id: ${isSelected.id}`
                  );
                }
                if (position && !inputFieldConfigMap[position.id]) {
                  throw new Error(
                    `2 Input field config not found for input field id: ${position.id}`
                  );
                }
                if (index && !inputFieldConfigMap[index.id]) {
                  throw new Error(`3 Input field config not found for input field id: ${index.id}`);
                }
                const eventName: CohortBuilderInputFieldProps = {
                  ...eventNameValue,
                  ...inputFieldConfigMap[eventNameValue.id],
                  onChange: (_value: string) => {
                    !viewMode &&
                      onEditCohortBuilderEventInputFieldValue(
                        eventFilterId,
                        DerivedEventInputFieldTypeLegacy.EVENT_NAME,
                        _value
                      );
                  },
                  viewMode,
                };
                const eventDate: CohortBuilderInputFieldProps | undefined = {
                  ...eventDateAttribute,
                  ...inputFieldConfigMap[eventDateAttribute.id],
                  onChange: (_value: string) => {
                    !viewMode &&
                      onEditCohortBuilderEventInputFieldValue(
                        eventFilterId,
                        DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE,
                        _value
                      );
                  },
                  viewMode,
                };
                const selection: CohortBuilderInputFieldProps | undefined =
                  type === DerivedEventTypeLegacy.SEQUENCE && isSelected !== undefined
                    ? {
                        ...isSelected,
                        ...inputFieldConfigMap[isSelected.id],
                        onChange: (_value: string) => {
                          onEditCohortBuilderEventInputFieldValue(
                            eventFilterId,
                            DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED,
                            _value
                          );
                        },
                        viewMode,
                      }
                    : undefined;
                const _position: CohortBuilderInputFieldProps | undefined =
                  type === DerivedEventTypeLegacy.INSTANCE && position !== undefined
                    ? {
                        ...position,
                        ...inputFieldConfigMap[position.id],
                        onChange: (_value: string) => {
                          onEditCohortBuilderEventInputFieldValue(
                            eventFilterId,
                            DerivedEventInputFieldTypeLegacy.EVENT_POSITION,
                            _value
                          );
                        },
                        viewMode,
                      }
                    : undefined;
                const _index: CohortBuilderInputFieldProps | undefined =
                  type === DerivedEventTypeLegacy.INSTANCE && index !== undefined
                    ? {
                        ...index,
                        ...inputFieldConfigMap[index.id],
                        onChange: (_value: string) => {
                          onEditCohortBuilderEventInputFieldValue(
                            eventFilterId,
                            DerivedEventInputFieldTypeLegacy.EVENT_INDEX,
                            _value
                          );
                        },
                        viewMode,
                      }
                    : undefined;
                const inputFields: CohortBuilderInputFieldProps[] = [eventName, eventDate];
                _position && inputFields.push(_position);
                _index && inputFields.push(_index);
                return (
                  <CohortBuilderEditModalEvent
                    id={eventFilterId}
                    key={eventFilterId}
                    fields={inputFields}
                    selection={selection}
                    isRemoveEnabled={eventFilters.length > 2}
                    onRemoveEvent={onDeleteCohortBuilderEventClick}
                    viewMode={viewMode}
                    modal={modal}
                    updateFieldError={updateFieldError}
                  />
                );
              }
            )}
          </CohortBuilderEditModalFilterSection>
        </Box>
      )}

      {numberOfEventFilters > 0 && (
        <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
      )}

      {/* render temporalConstraints */}
      {temporalConstraints.length > 0 && (
        <Box data-testid={'derivedEventFilters'} ref={temportalConstraintRef}>
          <CohortBuilderEditModalFilterSection
            id="temporalConstraints"
            key="temporalConstraints"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.temporalConstraintsSection.title',
            })}
            viewMode={viewMode}
          >
            {temporalConstraints.map(({ id: temporalConstraintId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={temporalConstraintId}
                  id={temporalConstraintId}
                  onRemoveFilter={onDeleteTemporalConstraintClick}
                  viewMode={viewMode}
                >
                  {inputFields.map(({ id: inputFieldId, value }) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const { inputType, width, label, options, placeholder, helperText } =
                      inputFieldConfigMap[inputFieldId];
                    const onChange = (_value: string) => {
                      !viewMode &&
                        onFilterInputValueChange(
                          temporalConstraintId,
                          inputFieldId,
                          _value,
                          FilterTypeLegacy.TEMPORAL
                        );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`subjectVariableEditCard-temporalConstraintsFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
        </Box>
      )}

      {uniquenessFilters.length > 0 && (
        <Box data-testid="uniquencessFilterSection" ref={uniquenessFilterRef}>
          <CohortBuilderEditModalFilterSection
            id="uniqueness-filters"
            key="uniqueness-filters"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.derivedEventsEditModal.uniqueFilterSection.title',
            })}
            viewMode={viewMode}
          >
            {uniquenessFilters.map(({ id: uniquenessFilterId, inputFields }) => {
              return (
                <CohortBuilderEditModalFilter
                  key={uniquenessFilterId}
                  id={uniquenessFilterId}
                  onRemoveFilter={onDeleteUniquenessFilterClick}
                  viewMode={viewMode}
                >
                  {inputFields.map(({ id: inputFieldId, value }) => {
                    if (!inputFieldConfigMap[inputFieldId]) {
                      throw new Error(
                        `Input field config not found for input field id: ${inputFieldId}`
                      );
                    }
                    const {
                      inputType,
                      width,
                      label,
                      options: _options,
                      placeholder,
                      helperText,
                    } = inputFieldConfigMap[inputFieldId];

                    const options = (_options as DropdownListItem[])?.filter((option) => {
                      if (label === 'Filter type') {
                        return !(option.value === 'MINIMUM_BETWEEN');
                      }

                      return true;
                    });

                    const onChange = (_value: string) => {
                      onFilterInputValueChange(
                        uniquenessFilterId,
                        inputFieldId,
                        _value,
                        FilterTypeLegacy.UNIQUENESS
                      );
                    };
                    return (
                      <CohortBuilderInputField
                        key={`simpleEventEditCard-uniquenessFilter-${inputFieldId}`}
                        id={inputFieldId}
                        value={value}
                        inputType={inputType}
                        width={width}
                        label={label}
                        options={options}
                        placeholder={placeholder}
                        helperText={helperText}
                        onChange={onChange}
                        viewMode={viewMode}
                      />
                    );
                  })}
                </CohortBuilderEditModalFilter>
              );
            })}
          </CohortBuilderEditModalFilterSection>
          <Box sx={(theme) => ({ height: theme.spacing(4), width: theme.spacing(4) })} />
        </Box>
      )}
    </ApiWorkflowDialog>
  );
};
